<template>
  <div
    v-if="!hasView"
    class="flex h-full flex-col items-center justify-center gap-3 py-20 text-center"
  >
    <div
      class="flex h-14 w-14 items-center justify-center rounded-full bg-red-50 text-red-500"
    >
      <svg-icon name="huge-unlock" class="h-6 w-6" />
    </div>
    <div class="text-lg font-semibold text-foreground">
      {{ $t("Нет доступа") }}
    </div>
  </div>

  <div v-else class="flex h-full flex-col gap-6">
    <!-- Filters -->
    <div class="flex flex-wrap gap-3">
      <div class="relative" style="width: 320px">
        <Search
          class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
        />
        <Input
          v-model="filters.q"
          :placeholder="$t('Введите ИИН (12 цифр)')"
          class="pl-9"
          maxlength="12"
          inputmode="numeric"
          @input="onSearch"
        />
      </div>

      <div class="w-[200px]">
        <Select v-model="filters.status">
          <SelectTrigger>
            <SelectValue :placeholder="$t('Статус устройства')" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">{{ $t("Все статусы") }}</SelectItem>
            <SelectItem
              v-for="(label, value) in DeviceStatusLabel"
              :key="value"
              :value="value"
            >
              {{ label }}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>

    <!-- Table -->
    <div class="table-area">
      <div class="h-full overflow-auto">
        <Table :loading="loading" :scroll-reset-key="filters.page">
          <TableHeader>
            <TableRow>
              <TableHead class="min-w-[240px]">
                {{ $t("Пользователь") }}
              </TableHead>
              <TableHead class="min-w-[260px]">Device ID</TableHead>
              <TableHead class="min-w-[140px]">IP</TableHead>
              <TableHead class="min-w-[120px]">{{ $t("Статус") }}</TableHead>
              <TableHead class="min-w-[180px]">
                {{ $t("Изменён статус") }}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableEmpty v-if="!loading && !list.length" :colspan="6">
              {{ $t("Нет данных") }}
            </TableEmpty>
            <TableRow
              v-for="row in list"
              :key="row.id"
              class="cursor-pointer"
              @click="openDetails(row)"
            >
              <TableCell>
                <div class="flex items-center gap-2.5">
                  <UserAvatar
                    :seed="row.id || row.user_identifier || ''"
                    :name="row.user_fio || row.user_identifier || ''"
                    :size="32"
                  />
                  <div class="min-w-0">
                    <div class="truncate text-sm font-medium text-foreground">
                      {{ row.user_fio || "—" }}
                    </div>
                    <div class="truncate text-xs text-muted-foreground">
                      {{ row.user_identifier || "—" }}
                    </div>
                  </div>
                </div>
              </TableCell>
              <TableCell>
                <span class="">{{ truncateId(row.device_id) }}</span>
              </TableCell>
              <TableCell class="">
                {{ row.ip_address || "—" }}
              </TableCell>
              <TableCell>
                <Tag
                  :variant="statusVariant(row.status)"
                  size="sm"
                  class="rounded-md"
                >
                  {{ DeviceStatusLabel[row.status] || row.status }}
                </Tag>
              </TableCell>
              <TableCell class="text-sm text-muted-foreground">
                {{ formatDate(row.status_changed_at) }}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <TableFooterWrapper
        :total="total"
        v-model:pageSize="filters.page_size"
        v-model:currentPage="filters.page"
        :pageSizes="[10, 20, 50]"
        :page-size="filters.page_size"
      >
        <template #pagination>
          <Pagination
            v-if="total > filters.page_size"
            :total="total"
            :items-per-page="filters.page_size"
            :page="filters.page"
            :sibling-count="1"
            :disabled="loading"
            show-edges
            @update:page="(p) => (filters.page = p)"
          >
            <PaginationContent v-slot="{ items }">
              <PaginationPrevious />
              <template v-for="(item, index) in items" :key="index">
                <PaginationItem
                  v-if="item.type === 'page'"
                  :value="item.value"
                  :is-active="item.value === filters.page"
                />
                <PaginationEllipsis v-else />
              </template>
              <PaginationNext />
            </PaginationContent>
          </Pagination>
        </template>
      </TableFooterWrapper>
    </div>

    <DeviceDetailsDrawer
      v-if="dialogOpen"
      v-model:open="dialogOpen"
      :device="selectedDevice"
      @updated="onDeviceUpdated"
    />
  </div>
</template>

<script>
import { Search } from "lucide-vue-next";
import { Input } from "@/shared/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { Tag } from "@/shared/ui/tag";
import {
  DeviceStatusLabel,
  DeviceStatusVariant,
} from "@/shared/config/deviceStatuses";
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/ui/pagination";
import TableFooterWrapper from "@/shared/ui/custom/components/TableFooterWrapper.vue";
import TableRowActions from "@/shared/ui/custom/components/TableRowActions.vue";
import dateFormat from "@/shared/lib/date-format";
import { getUserDevices } from "@/entities/user-device";
import DeviceDetailsDrawer from "./components/DeviceDetailsDrawer.vue";
import UserAvatar from "./face-id/UserAvatar.vue";

export default {
  name: "DevicesTab",
  components: {
    Search,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Tag,
    Table,
    TableBody,
    TableCell,
    TableEmpty,
    TableHead,
    TableHeader,
    TableRow,
    Pagination,
    PaginationContent,
    PaginationEllipsis,
    PaginationItem,
    PaginationNext,
    PaginationPrevious,
    TableFooterWrapper,
    TableRowActions,
    DeviceDetailsDrawer,
    UserAvatar,
  },
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      filters: {
        q: "",
        status: "ALL",
        page: 1,
        page_size: 20,
      },
      searchTimer: null,
      dialogOpen: false,
      selectedDevice: null,
    };
  },
  computed: {
    DeviceStatusLabel() {
      return DeviceStatusLabel;
    },
    hasView() {
      return this.$store.getters.has_access("AVU");
    },
    rowActions() {
      return [
        {
          label: this.$t("Подробнее"),
          type: "default",
          action: "view",
          icon: "huge-eye",
          isLast: true,
        },
      ];
    },
  },
  watch: {
    "filters.status"() {
      this.filters.page = 1;
      this.load();
    },
    "filters.page"() {
      this.load();
    },
    "filters.page_size"() {
      this.filters.page = 1;
      this.load();
    },
  },
  mounted() {
    if (this.hasView) this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      const params = {
        page: this.filters.page,
        page_size: this.filters.page_size,
      };
      const q = (this.filters.q || "").trim();
      if (q) params.identifier = q;
      if (this.filters.status && this.filters.status !== "ALL") {
        params.status = this.filters.status;
      }
      try {
        const data = await getUserDevices(params);
        if (data?.success && data?.payload) {
          this.list = data.payload.items || [];
          this.total = Number(data.payload.total) || 0;
        } else {
          this.list = [];
          this.total = 0;
        }
      } finally {
        this.loading = false;
      }
    },
    onSearch() {
      // санитайзер: оставляем только цифры, максимум 12
      const sanitized = (this.filters.q || "").replace(/\D/g, "").slice(0, 12);
      if (sanitized !== this.filters.q) {
        this.filters.q = sanitized;
      }
      clearTimeout(this.searchTimer);
      // фильтр срабатывает только при полном ИИН (12 цифр) или при пустом
      // значении (для сброса фильтра). Промежуточные длины игнорируем.
      if (sanitized.length !== 0 && sanitized.length !== 12) return;
      this.searchTimer = setTimeout(() => {
        this.filters.page = 1;
        this.load();
      }, 300);
    },
    truncateId(id) {
      if (!id) return "—";
      if (id.length <= 16) return id;
      return id.slice(0, 12) + "…" + id.slice(-4);
    },
    statusVariant(status) {
      return DeviceStatusVariant[status] || "outline";
    },
    formatDate(value) {
      if (!value) return "—";
      return dateFormat.timestamp_to_local_tz(value, "DD MMM YYYY, HH:mm");
    },
    openDetails(row) {
      this.selectedDevice = row;
      this.dialogOpen = true;
    },
    onDeviceUpdated() {
      this.load();
    },
  },
};
</script>

<style lang="scss" scoped>
.table-area {
  @apply flex flex-col gap-3 flex-1 min-h-0;
}
</style>
